import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './calendarView/calendarStyle.css';
import CalendarToolbar from './calendarView/CalendarToolbar';
import { getDefaultCalendarView, CALENDAR_VIEW_DAY } from './calendarView/Calendar';
import moment from 'moment-timezone';
import { withTranslation } from 'react-i18next';
import ReduxBlockUi from 'react-block-ui/redux';
import 'react-block-ui/style.css';
import CalendarWeekView from './calendarView/CalendarWeekView';
import CalendarDayView from './calendarView/CalendarDayView';
import CalendarEventWrapper from './calendarView/CalendarEventWrapper';
import CalendarEventContainerWrapper from './calendarView/CalendarEventContainerWrapper';
import utils from '../../common/services/utils';

const localizer = momentLocalizer(moment);

class IndividualConsultationCalendar extends Component {
	constructor(props) {
		super(props);
		this.handleCalendarNavigate = this.handleCalendarNavigate.bind(this);
		this.handleCalendarEventSelect = this.handleCalendarEventSelect.bind(this);

		this.state = {
			selectedScheduleItem: null
		};
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.schedule && nextProps.center && nextProps.date && nextProps.initialDate && moment(nextProps.date).format('YYYYMMDD') === moment(nextProps.initialDate).format('YYYYMMDD')) {
			// move to the first date with classes when opening in a mobile view and there are no classes on the initial date
			const nextView = getDefaultCalendarView(true);
			if (nextView === CALENDAR_VIEW_DAY) {
				const enableUserTimeZone = nextProps.enableUserTimeZone;
				const center = nextProps.center;
				const currentDate = moment(nextProps.date);
				const sortedLessons = nextProps.schedule.sort((a, b) => moment(a.start) - moment(b.start));
				const currentDateLesson = sortedLessons.find(x => (enableUserTimeZone ? utils.convertToUserTime(x.start, center) : moment(x.start)).format('YYYYMMDD') === currentDate.format('YYYYMMDD'));
				const futureDateLesson = sortedLessons.find(x => (enableUserTimeZone ? utils.convertToUserTime(x.start, center) : moment(x.start)).startOf('day').isAfter(currentDate));
				if (!currentDateLesson && futureDateLesson) {
					this.handleCalendarNavigate(new Date(moment(futureDateLesson.start)));
				}
			}
		}
	}

	handleCalendarNavigate(date) {
		this.props.onNavigate(date);
	}

	handleCalendarEventSelect(event) {
		if (!event.item.booked) {
			this.props.onSelectConsultationDate(event.item.start);
			this.setState({ selectedScheduleItem: event.item });
		}
	}

	getScheduleItemCss(event) {
		const scheduleItem = event.item;
		const { selectedScheduleItem } = this.state;
		const isSelected = selectedScheduleItem ? selectedScheduleItem.start === scheduleItem.start : this.props.selectedDate === scheduleItem.start;

		return { className: `${scheduleItem.booked ? 'red' : 'green'} ${isSelected ? 'selected' : ''}` };
	}

	render() {
		const { center, schedule, enableUserTimeZone, calendarBlock, calendarUnblock, date, initialDate, t } = this.props;

		schedule.forEach(item => {
			item.startMoment = enableUserTimeZone && center ? utils.convertToUserTime(item.start, center) : moment(item.start);
			item.endMoment = enableUserTimeZone && center ? utils.convertToUserTime(item.end, center) : moment(item.end);
		});

		const sortedSchedule = schedule.sort(function (a, b) {
			return a.startMoment.format('HH') - b.startMoment.format('HH');
		});

		const hasMultiDayEvent = sortedSchedule.length > 0 ? sortedSchedule.some(x => moment(x.startMoment).startOf('day').isBefore(moment(x.endMoment).startOf('day'))) : false;

		const hourStart = sortedSchedule.length > 0 ? sortedSchedule[0].startMoment.hour() : 9;
		const hourEnd = sortedSchedule.length > 0 ? hasMultiDayEvent ? 23 : Math.max(20, ...sortedSchedule.map(x => x.endMoment.hour())) : 20;

		const bigCalendarEvents = schedule.map(item => ({
			allDay: false,
			start: new Date(item.startMoment),
			end: new Date(item.endMoment),
			title: item.booked ? t('FO_Booking_IndividualConsultation_Booked') :
				`${item.startMoment.format('HH:mm')} ${t('FO_Booking_IndividualConsultation_Available')}`,
			item
		}));

		const formats = {
			dayFormat: (date, culture, localizer) =>
				localizer.format(date, 'ddd DD/MM', culture),
			eventTimeRangeFormat: () => ''	// hide time range label, only title will show (i.e. start time of the consultation)
		};

		return (
			<div>
				<ReduxBlockUi block={calendarBlock} unblock={calendarUnblock}>
					<div className='calendar-wrapper individual-consultation-calendar'>
						<BigCalendar
							date={date}
							defaultView={getDefaultCalendarView(true)}
							min={moment().startOf('day').hour(hourStart).toDate()}
							max={moment().endOf('day').hour(hourEnd).toDate()}
							events={bigCalendarEvents}
							onNavigate={this.handleCalendarNavigate}
							onSelectEvent={this.handleCalendarEventSelect}
							components={{
								toolbar: CalendarToolbar({ initialDate }),
								eventWrapper: CalendarEventWrapper({ hourStart }),
								eventContainerWrapper: CalendarEventContainerWrapper({ hourStart, hourEnd, timeslotHeight: 50 })
							}}
							messages={{ allDay: '' }}
							eventPropGetter={(event) => this.getScheduleItemCss(event)}
							views={{ day: CalendarDayView, week: CalendarWeekView }}
							localizer={localizer}
							formats={formats}
							showMultiDayTimes
							// subtract one millisecond from the end date putting midnight dates at 11:59:999
							endAccessor={({ end }) => new Date(end.getTime() - 1)}
						/>
					</div>
				</ReduxBlockUi>
			</div >
		);
	}
}

IndividualConsultationCalendar.propTypes = {
	date: PropTypes.object,
	initialDate: PropTypes.object,
	center: PropTypes.shape({
		id: PropTypes.number,
		utcOffset: PropTypes.number
	}),
	schedule: PropTypes.arrayOf(PropTypes.shape({
		scheduleId: PropTypes.number,
		start: PropTypes.string,
		end: PropTypes.string
	})),
	selectedDate: PropTypes.string,
	enableUserTimeZone: PropTypes.bool,
	onNavigate: PropTypes.func.isRequired,
	onSelectConsultationDate: PropTypes.func.isRequired
};

export default withTranslation()(IndividualConsultationCalendar);